import has from 'lodash/has';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FormattedHTMLMessage } from '@bibliocommons/react-intl';
import { bindAll } from '@bibliocommons/utils-react';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { fixedEncodeURIComponent } from 'app/helpers/url';
import { Angle } from '@bibliocommons/deprecated-base-icons';
import { SEARCH_TYPES } from 'app/constants/SearchConstants';

import './ExternalSearchLink.scss';

function getLocalizationKey(searchType) {
  switch (searchType) {
    case SEARCH_TYPES.TITLE:
      return 'external_search_link_title';
    case SEARCH_TYPES.AUTHOR:
      return 'external_search_link_author';
    case SEARCH_TYPES.SUBJECT:
      return 'external_search_link_subject';
    case SEARCH_TYPES.KEYWORD:
    case SEARCH_TYPES.CATALOG:
    case SEARCH_TYPES.SMART:
      return 'external_search_link_keyword';
    default:
      return '';
  }
}

function screenReaderMessage(linkText) {
  return (
    <ScreenReaderMessage>
      <FormattedMessage id="ada_opens_a_new_tab" values={{ linkText }} />
    </ScreenReaderMessage>
  );
}

export class ExternalSearchLink extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  getExternalSearchUrl() {
    const { intl, query, searchType } = this.props;
    const backlink = __CLIENT__ ? window.location.href : '';

    return has(intl.messages, getLocalizationKey(searchType))
      ? intl.formatMessage(
          { defaultMessage: '', id: getLocalizationKey(searchType) },
          { q: fixedEncodeURIComponent(query), backlink }
        )
      : null;
  }

  getFindOutMoreAdditionalLink() {
    const { intl } = this.props;
    return has(intl.messages, 'find_out_more_additional_link')
      ? intl.formatMessage({ id: 'find_out_more_additional_link' })
      : null;
  }

  renderFormattedExternalSearch(linkText) {
    const { intl } = this.props;
    return (
      <div className="cp-external-search-link">
        <div className="external-search-name">{linkText}</div>
        <a
          href={this.getExternalSearchUrl()}
          className="external-search-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage id="search_link_text" />
          {screenReaderMessage(linkText)}
          <Angle.Right />
        </a>
        <div className="additional-link">
          {this.getFindOutMoreAdditionalLink(intl) && (
            <a
              href={this.getFindOutMoreAdditionalLink(intl)}
              className="find_out_more_additional_link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span aria-hidden>
                <FormattedMessage id="find_out_more_link" />
              </span>
              {screenReaderMessage(<FormattedMessage id="find_out_more_link" />)}
            </a>
          )}
        </div>
      </div>
    );
  }

  renderExternalSearchLinkOnly(linkText) {
    return (
      <a
        href={this.getExternalSearchUrl()}
        className="cp-external-search-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span aria-hidden>{linkText}</span>
        {screenReaderMessage(linkText)}
        <Angle.Right />
      </a>
    );
  }

  render() {
    const { intl, formatted } = this.props;

    if (!this.getExternalSearchUrl()) {
      return null;
    }

    const linkText = (
      <span>
        <FormattedHTMLMessage id="external_search_link_line1" defaultMessage="" />
        &nbsp;
        {intl.messages.external_search_link_line2 ? (
          <FormattedHTMLMessage id="external_search_link_line2" defaultMessage="" />
        ) : null}
        &nbsp;
      </span>
    );

    return formatted ? this.renderFormattedExternalSearch(linkText) : this.renderExternalSearchLinkOnly(linkText);
  }
}

ExternalSearchLink.defaultProps = {
  formatted: false
};

ExternalSearchLink.propTypes = {
  query: PropTypes.string.isRequired,
  searchType: PropTypes.string.isRequired,
  formatted: PropTypes.bool
};

ExternalSearchLink.displayName = 'ExternalSearchLink';

export default injectIntl(ExternalSearchLink);
